import { Star } from "lucide-react";
import { useMemo, useState } from "react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { cn } from "@/lib/utils";
import { useGetFavoriteStars, useStarFavorite, useUnstarFavorite } from "@/queries/favorites";
import { EntityType } from "@/queries/folder-entities/types";

const formatStarCount = (count: number): string => {
  if (count >= 1000) {
    const k = count / 1000;
    return k % 1 === 0 ? `${k}K` : `${k.toFixed(1)}K`;
  }
  return String(count);
};

interface EntityStarButtonProps {
  entityType: EntityType;
  entityId: string | number;
  workspaceId?: number;
}

const EntityStarButton = ({ entityType, entityId, workspaceId }: EntityStarButtonProps) => {
  const auth = useAuth();
  const user = useUser();
  const resolvedWorkspaceId = workspaceId ?? user.activeWorkspaceId ?? 0;
  const currentUserId = auth?.user?.id;
  const normalizedEntityId = String(entityId);
  const [isHoverOpen, setIsHoverOpen] = useState(false);

  const { data: starsData, isLoading } = useGetFavoriteStars({
    workspaceId: resolvedWorkspaceId,
    entityType,
    entityId: normalizedEntityId,
    enabled: !!resolvedWorkspaceId && !!normalizedEntityId && normalizedEntityId !== "-1",
  });

  const starFavorite = useStarFavorite();
  const unstarFavorite = useUnstarFavorite();

  const isStarred = useMemo(
    () => starsData?.users.some(starUser => starUser.id === currentUserId) ?? false,
    [starsData, currentUserId],
  );

  const totalStars = starsData?.total ?? 0;
  const isMutating = starFavorite.isLoading || unstarFavorite.isLoading;

  const handleToggleStar = () => {
    if (isMutating || isLoading || !resolvedWorkspaceId) {
      return;
    }

    const payload = {
      workspaceId: resolvedWorkspaceId,
      entityType,
      entityId: normalizedEntityId,
    };

    if (isStarred) {
      unstarFavorite.mutate(payload);
    } else {
      starFavorite.mutate(payload);
    }
  };

  if (!resolvedWorkspaceId || normalizedEntityId === "-1" || normalizedEntityId === "") {
    return null;
  }

  return (
    <Popover open={isHoverOpen} onOpenChange={setIsHoverOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={handleToggleStar}
          onMouseEnter={() => {
            if (totalStars > 0) {
              setIsHoverOpen(true);
            }
          }}
          onMouseLeave={() => setIsHoverOpen(false)}
          className="flex flex-shrink-0 items-center gap-x-1 rounded-md px-1.5 py-1 text-sm text-gray-500 transition-colors hover:bg-gray-100 hover:text-yellow-500 focus:outline-none"
        >
          <Star
            className={cn("h-4 w-4", isStarred && "fill-yellow-500 text-yellow-500")}
            strokeWidth={2}
          />
          {totalStars > 0 && <span className="text-gray-500">{formatStarCount(totalStars)}</span>}
        </button>
      </PopoverTrigger>
      {totalStars > 0 && (
        <PopoverContent
          className="w-auto min-w-[160px] max-w-[240px] p-2"
          side="bottom"
          align="start"
          sideOffset={0}
          onMouseEnter={() => setIsHoverOpen(true)}
          onMouseLeave={() => setIsHoverOpen(false)}
          onOpenAutoFocus={e => e.preventDefault()}
        >
          <p className="mb-1 px-1 text-xs font-medium text-gray-500">Starred by</p>
          <ul className="flex flex-col gap-y-0.5">
            {starsData?.users.map(starUser => (
              <li
                key={starUser.id}
                className="truncate rounded px-1 py-0.5 text-sm text-gray-700"
                title={starUser.email}
              >
                {starUser.name || starUser.email}
              </li>
            ))}
            {starsData && starsData.total > starsData.users.length && (
              <li className="px-1 py-0.5 text-xs text-gray-400">
                and {starsData.total - starsData.users.length} more...
              </li>
            )}
          </ul>
        </PopoverContent>
      )}
    </Popover>
  );
};

export default EntityStarButton;
